/**
 * PhaseTransitionModal Component
 *
 * Reusable confirmation modal for session phase transitions.
 * Config-driven: caller provides title, description, confirm label, and color.
 * Handles loading state during async DB write and error/retry on failure.
 *
 * @see SessionDraftService.updateStatus — DB persistence called by parent
 */

// 1. External dependencies
import React, { useState, useCallback } from 'react';

// 2. Internal absolute imports
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from '@enfomentis/ui';

// ===== TYPES =====

export interface PhaseTransitionModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal closes (cancel or successful confirm) */
  onClose: () => void;
  /** Async confirm handler — returns true on success, false on failure */
  onConfirm: () => Promise<boolean>;
  /** Modal header title (e.g., "End Trading Session?") */
  title: string;
  /** Descriptive body text explaining the transition */
  description: string;
  /** Confirm button label (e.g., "End Session") */
  confirmLabel: string;
  /** Confirm button color */
  confirmColor: 'primary' | 'danger' | 'warning';
}

// ===== COMPONENT =====

/**
 * PhaseTransitionModal — confirmation dialog for phase transitions.
 *
 * Flow:
 * 1. Trader presses confirm → loading state activates
 * 2. `onConfirm()` awaited (parent calls handlePhaseChange → DB write)
 * 3. On success → modal closes
 * 4. On failure → error message shown, retry or cancel available
 */
export const PhaseTransitionModal: React.FC<PhaseTransitionModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel,
  confirmColor,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const handleConfirm = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setHasError(false);

    const success = await onConfirm();

    setIsLoading(false);

    if (success) {
      onClose();
    } else {
      setHasError(true);
    }
  }, [onConfirm, onClose]);

  const handleClose = useCallback((): void => {
    if (isLoading) return;
    setHasError(false);
    onClose();
  }, [isLoading, onClose]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="sm"
      placement="center"
      isDismissable={!isLoading}
      isKeyboardDismissDisabled={isLoading}
    >
      <ModalContent>
        <ModalHeader>{title}</ModalHeader>

        <ModalBody>
          <p className="text-sm text-text-secondary">
            {description}
          </p>
          {hasError && (
            <p className="text-sm text-danger mt-2">
              Failed to update session status. Please try again.
            </p>
          )}
        </ModalBody>

        <ModalFooter>
          <Button variant="light" onPress={handleClose} isDisabled={isLoading}>
            Cancel
          </Button>
          <Button
            color={confirmColor}
            onPress={handleConfirm}
            isLoading={isLoading}
          >
            {confirmLabel}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
